import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { Link as ScrollLink } from "react-scroll";
import { useTranslation } from "react-i18next";

import bossieWithoutO from "../assets/images/bossie_without_o.png";
import oLogo from "../assets/images/o.png";
import bossie from "../assets/images/bossie.png";
import LanguageSwitcher from "./language-switcher";

interface NavbarProps {
  navLight: boolean;
  playBtn?: boolean;
  bgLight?: boolean;
  navCenter?: boolean;
}

interface NavItem {
  key: string;
  to: string;
  label: string;
}

const SCROLL_THRESHOLD = 50;
const SCROLL_DURATION = 200;

export default function Navbar({ navLight }: NavbarProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { t } = useTranslation();

  // Memoize navigation items to prevent recreating on every render
  const navItems: NavItem[] = useMemo(
    () => [
      { key: "home", to: "home", label: t("navbar.home") },
      { key: "features", to: "features", label: t("navbar.features") },
      { key: "application", to: "screenshot", label: t("navbar.application") },
      { key: "faqs", to: "faqs", label: "FAQs" },
      { key: "contact", to: "contact", label: t("navbar.contact") },
    ],
    [t]
  );

  // Optimized scroll handler with useCallback
  const handleScroll = useCallback(() => {
    const scrollY = window.scrollY;
    setIsScrolled(scrollY > SCROLL_THRESHOLD);
  }, []);

  // Throttled scroll handler for better performance
  const throttledScrollHandler = useMemo(() => {
    let ticking = false;
    return () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };
  }, [handleScroll]);

  useEffect(() => {
    // Set initial scroll state
    setIsScrolled(window.scrollY > SCROLL_THRESHOLD);

    window.addEventListener("scroll", throttledScrollHandler, {
      passive: true,
    });
    return () => window.removeEventListener("scroll", throttledScrollHandler);
  }, [throttledScrollHandler]);

  const toggleMobileMenu = useCallback(() => {
    setIsMenuOpen((prev) => !prev);
  }, []);

  const closeMobileMenu = useCallback(() => {
    setIsMenuOpen(false);
  }, []);

  // Memoize computed classes
  const navbarClasses = useMemo(
    () =>
      `navbar fixed left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled ? "top-0" : "top-0"
      }`,
    [isScrolled]
  );

  const containerClasses = useMemo(
    () =>
      `relative mx-auto max-w-6xl 
     sm:bg-white/70 sm:dark:bg-slate-900/80 sm:backdrop-blur-2xl
     sm:border sm:border-white/30 sm:dark:border-slate-700/30
     transition-all duration-500 ease-out
     ${isScrolled ? "py-4 sm:rounded-full" : "py-6 sm:rounded-2xl"}
     ${isMenuOpen ? "sm:rounded-3xl" : ""}
     ${!isScrolled ? "pb-12" : ""}`,
    [isScrolled, isMenuOpen]
  );

  const navLinkClasses =
    "nav-link block px-3 py-2 lg:px-4 lg:py-2 hover:text-primaryColor transition-all duration-200 rounded-full hover:bg-primaryColor/5 text-sm lg:text-base font-medium";
  const mobileNavLinkClasses =
    "nav-link block px-4 py-3 hover:text-primaryColor transition-all duration-200 rounded-xl hover:bg-primaryColor/5 text-base font-medium";

  // Render navigation items
  const renderNavItems = useCallback(
    (isMobile = false) =>
      navItems.map(({ key, to, label }) => (
        <li key={key} className="nav-item">
          <ScrollLink
            className={isMobile ? mobileNavLinkClasses : navLinkClasses}
            activeClass="active"
            spy={true}
            smooth={true}
            duration={SCROLL_DURATION}
            to={to}
            onClick={isMobile ? closeMobileMenu : undefined}
          >
            {label}
          </ScrollLink>
        </li>
      )),
    [navItems, closeMobileMenu]
  );

  // Logo component for better organization
  const LogoSection = useMemo(
    () =>
      !navLight && (
        <Link
          className="navbar-brand flex-shrink-0 group"
          to="/"
          aria-label="Bossie Home"
        >
          {/* Mobile: Complete Bossie logo */}
          <img
            src={bossie}
            className="h-7 sm:h-8 md:h-9 lg:h-10 block sm:hidden transition-all duration-300"
            alt="Bossie Logo"
            loading="eager"
          />
          {/* Desktop: Two-part logo with animation */}
          <div className="relative hidden sm:block">
            <img
              src={bossieWithoutO}
              className="h-7 sm:h-8 md:h-9 lg:h-10 inline-block dark:hidden transition-all duration-300"
              alt="Bossie Logo"
              loading="eager"
            />
            <img
              src={oLogo}
              className="absolute top-2.5 h-4 sm:h-4 md:h-5 lg:h-6 inline-block dark:hidden transition-all duration-800 group-hover:animate-spin-slow"
              style={{ left: "1.49rem" }}
              alt="Bossie O"
              loading="eager"
            />
          </div>
        </Link>
      ),
    [navLight]
  );

  // Promotional banner component
  const PromotionalBanner = useMemo(
    () =>
      !isScrolled && (
        <div
          className="mt-4 mb-0 sm:mb-0 px-0 sm:px-6 lg:px-8"
          role="banner"
          aria-label="Special offer"
        >
          <div className="relative overflow-hidden bg-gradient-to-r from-red-50 via-red-100 to-red-50 dark:from-red-900/30 dark:via-red-800/20 dark:to-red-900/30 sm:rounded-xl py-3 sm:border-2 sm:border-red-300 dark:sm:border-red-600/40 shadow-xl shadow-red-200/50 dark:shadow-red-900/20">
            {/* Animated background effect */}
            <div className="absolute inset-0 sm:rounded-xl bg-gradient-to-r from-transparent via-red-200/40 dark:via-red-600/30 to-transparent animate-pulse-slow" />

            <div className="relative flex items-center justify-center">
              <div className="animate-scroll-text whitespace-nowrap text-sm font-bold text-red-700 dark:text-red-300">
                <span className="inline-flex items-center gap-4">
                  <span className="font-extrabold text-red-800 dark:text-red-200">
                    {t("promotionalBanner.exclusiveOffer")}
                  </span>
                  <span className="font-semibold">
                    {t("promotionalBanner.offerText")}
                  </span>
                  <span className="font-extrabold text-red-800 dark:text-red-200 animate-pulse">
                    {t("promotionalBanner.callToAction")}
                  </span>
                </span>
              </div>
            </div>

            {/* Glowing effect */}
            <div className="absolute inset-0 sm:rounded-xl bg-gradient-to-r from-red-400/10 via-transparent to-red-400/10 animate-pulse-slow" />
          </div>
        </div>
      ),
    [isScrolled, t]
  );

  return (
    <nav
      className={navbarClasses}
      id="navbar"
      role="navigation"
      aria-label="Main navigation"
    >
      <div className="container relative mx-auto px-0 sm:px-4">
        <div
          className={containerClasses}
          style={{ boxShadow: "0 0 30px 10px rgba(0, 0, 0, 0.05)" }}
        >
          <div className="flex flex-col">
            <div className="flex items-center justify-between px-4 sm:px-6 lg:px-8">
              {/* Logo Section */}
              {LogoSection}

              {/* Desktop Navigation Menu */}
              <nav
                className="navigation hidden lg:flex flex-1 justify-center"
                role="navigation"
                aria-label="Desktop navigation"
              >
                <ul
                  className={`navbar-nav flex items-center gap-8 lg:gap-2 ${
                    navLight ? "nav-light" : ""
                  }`}
                >
                  {renderNavItems()}
                </ul>
              </nav>

              {/* Desktop Language Switcher */}
              <div className="hidden lg:block flex-shrink-0">
                <LanguageSwitcher />
              </div>

              {/* Mobile Menu Button */}
              <button
                type="button"
                className="lg:hidden inline-flex items-center justify-center w-10 h-10 text-slate-900 dark:text-white rounded-full hover:bg-white/20 dark:hover:bg-slate-800/20 transition-all duration-200 backdrop-blur-sm"
                onClick={toggleMobileMenu}
                aria-label={
                  isMenuOpen ? "Close navigation menu" : "Open navigation menu"
                }
                aria-expanded={isMenuOpen}
                aria-controls="mobile-menu"
              >
                <i
                  className={`mdi text-[20px] transition-all duration-300 ${
                    isMenuOpen ? "mdi-close" : "mdi-menu"
                  }`}
                  aria-hidden="true"
                />
              </button>
            </div>

            {/* Promotional Banner */}
            {PromotionalBanner}
          </div>

          {/* Mobile Menu Dropdown */}
          {isMenuOpen && (
            <div
              id="mobile-menu"
              className="lg:hidden absolute top-full left-0 right-0 mt-2 bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl shadow-2xl shadow-black/20 dark:shadow-black/40 rounded-3xl py-6 z-50 border border-white/20 dark:border-slate-700/20"
              role="dialog"
              aria-label="Mobile navigation menu"
            >
              <div className="flex justify-center mb-4">
                <LanguageSwitcher />
              </div>
              <nav role="navigation" aria-label="Mobile navigation">
                <ul className="flex flex-col px-2">{renderNavItems(true)}</ul>
              </nav>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}
